import {
  Component,
  OnInit,
  ViewChild,
  HostListener,
  OnDestroy,
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { AssessmentService } from "../shared/services/assessment.service";
import { UserService } from "../../shared/services/user.service";
import { Question } from "../shared/models/question";
import { ViewStateService } from "../../shared/services/view-state.service";
import { Subject, Subscription } from "rxjs";
import { CountdownComponent, CountdownEvent } from "ngx-countdown";
import { Assessment } from "../shared/models/assessment";
import { NotificationsService } from "../../shared/services/notifications.service";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "app/auth/auth.service";

@Component({
  selector: "app-question-view",
  templateUrl: "./question-view.component.html",
  styleUrls: ["./question-view.component.css"],
})
export class QuestionViewComponent implements OnInit, OnDestroy {
  personId: string;
  question: Question;
  questionId: string;
  assessment: Assessment;
  assessmentId: string;
  letters: Array<string>;
  tick: number;
  subscription: Subscription;
  isWarning: boolean = false;
  selectedQuestionOptionId: string;
  currentYear: string;
  private destroy$ = new Subject<void>();

  @ViewChild(CountdownComponent, { static: true }) counter: CountdownComponent;

  constructor(
    private router: Router,
    private userService: UserService,
    private route: ActivatedRoute,
    private assessmentService: AssessmentService,
    private viewState: ViewStateService,
    private notifications: NotificationsService,
    private authService: AuthService
  ) {
    this.question = new Question();
    this.letters = ["A", "B", "C", "D"];
  }

  ngOnInit() {
    history.pushState(null, null, location.href);
    window.onpopstate = function () {
      history.go(1);
    };
    this.personId = this.userService.CurrentUser.personId;
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.questionId = params["questionId"];
      this.assessmentId = params["assessmentId"];
      this.assessment = this.assessmentService.getAssessmentObj(
        this.assessmentId
      );
      this.startQuestion(this.assessmentService.getAssessmentResultId());
    });
    this.currentYear = new Date().getFullYear().toString();
  }

  private loadAssessmentQuestion() {
    this.assessmentService
      .getAssessmentQuestion(this.personId, this.questionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (question) => {
          this.question = question;

          this.authService
          .refreshToken()
          .pipe(takeUntil(this.destroy$))
          .subscribe((rt) => {
            });

          this.counter.restart();          
          this.viewState.stopLoading();
        },
        (errors) => {
          this.handleServerErrorResponse(errors);
        }
      );
  }

  handleCountDownEvent(e: CountdownEvent) {
    if (e.action==="notify") {
      this.onNotify(e);
    } else if (e.action==="done") {
      this.onTimeOut()
    } else {
      //console.log("Other event: "+ e.action);
    }
  }

  onNotify(event) {
    this.isWarning = true;
    //console.log("warning fired")
  }

  onTimeOut() {
    // Removing notfication.  Deemed unnecessary 2019.01.09.
    // this.notifications.error('Timeout', 'Sorry time is over');
    this.selectedQuestionOptionId = "0";
    this.assessmentService.setSelectedAnswer(null);
    this.submitAnswer("timedOut");
    //console.log("Time out fired")
  }

  private startQuestion(assessmentResultId: string) {
    this.viewState.startLoading();
    this.assessmentService
      .startQuestion(this.personId, this.questionId, assessmentResultId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.assessmentService.setQuestionResponseId(res.questionResponseId);
          this.loadAssessmentQuestion();
          this.router.navigate([
            "assessment/" + this.assessmentId + "/question/" + this.questionId,
          ]);
        },
        (errors) => {
          this.handleServerErrorResponse(errors);
        }
      );
  }

  selectQuestionOptionId(questionOptionId) {
    this.selectedQuestionOptionId = questionOptionId;
    this.assessmentService.setSelectedAnswer(questionOptionId);
  }

  submitAnswer(reason: string) {
    this.viewState.startLoading();
    let questionResponseId = this.assessmentService.getQuestionResponseId();
    this.assessmentService
      .answerQuestion(
        this.personId,
        this.selectedQuestionOptionId,
        questionResponseId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (answer) => {
          this.viewState.stopLoading();
          if (reason == "timedOut") {
            this.router.navigate([
              "assessment/" +
                this.assessmentId +
                "/question/" +
                this.questionId +
                "/answer",
            ]);
          } else {
            this.router.navigate([
              "assessment/" +
                this.assessmentId +
                "/question/" +
                this.questionId +
                "/confidence-relevance",
            ]);
          }
        },
        (errors) => {
          this.handleServerErrorResponse(errors);
        }
      );
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500)
      this.notifications.error(
        "Unexpected Error",
        "An unexpected error occurred. Please try again or contact NBRC for assistance."
      );
    else {
      let error = response;
      this.notifications.error("Error", error[0].message);
    }
    this.router.navigate(["/assessment"]);
    this.viewState.stopLoading();
  }

  blockCopy() {
    this.assessmentService
      .warnUser(this.personId, "copy", this.questionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.notifications.errorWithWait(
            "Warning",
            "Your key strokes are recorded by the system. Efforts to capture content could initiate an investigation of potential misconduct by the NBRC Judicial and Ethics Committee.",
            0
          );
        },
        (err) => {
          console.log(err);
        }
      );
  }

  blockCut() {
    this.assessmentService
      .warnUser(this.personId, "cut", this.questionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.notifications.errorWithWait(
            "Warning",
            "Your key strokes are recorded by the system. Efforts to capture content could initiate an investigation of potential misconduct by the NBRC Judicial and Ethics Committee.",
            0
          );
        },
        (err) => {
          console.log(err);
        }
      );
  }

  @HostListener("window:beforeunload", ["$event"])
  beforeunloadHandler(event) {
    return (event.returnValue =
      "You have started question complete it first, If you leave, your will get 0 .");
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
